/**
 * Completion View Component
 *
 * Shown when quiz is completed. Displays stats and provides restart/exit options.
 * Can be minimized to a prompt bar to explore the map.
 */

import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface QuizStats {
  totalQuestions: number;
  answeredQuestions: number;
  correctAnswers: number;
  incorrectAnswers: number;
  accuracyRate: number;
}

interface CompletionViewProps {
  stats: QuizStats;
  onRestart: () => void;
  onExploreMap: () => void;
  onExit: () => void;
  onViewKnowledge: () => void;
  onEditQuiz?: () => void;
  showLabels: boolean;
  onToggleLabels: () => void;
}

export function CompletionView({
  stats,
  onRestart,
  onExploreMap,
  onExit,
  onViewKnowledge,
  onEditQuiz,
  showLabels,
  onToggleLabels,
}: CompletionViewProps) {
  const [isMinimized, setIsMinimized] = useState(false);

  // Minimized view - compact prompt bar
  if (isMinimized) {
    return (
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-dropdown w-full max-w-4xl px-4 pointer-events-none">
        <Card className="bg-card/95 backdrop-blur-md shadow-xl transition-all duration-300 pointer-events-auto">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              {/* Stats Summary */}
              <div className="flex items-center gap-4 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-3xl">🎉</span>
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground">Quiz Complete</h3>
                    <p className="text-xl font-bold text-primary">
                      {Math.round(stats.accuracyRate)}% Accuracy
                    </p>
                  </div>
                </div>
                <div className="hidden sm:block text-sm text-muted-foreground">
                  {stats.correctAnswers}/{stats.totalQuestions} correct
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant={showLabels ? "default" : "outline"}
                  onClick={onToggleLabels}
                  className="h-8"
                  title={showLabels ? "Hide answers" : "Show answers"}
                >
                  {showLabels ? "👁️" : "👁️"}
                </Button>

                <Button size="sm" onClick={onRestart} className="h-8">
                  🔄 Play Again
                </Button>

                {/* More Actions Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-8 p-0"
                      title="More actions"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onEditQuiz && (
                      <DropdownMenuItem onClick={onEditQuiz}>
                        ✏️ Edit Quiz
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={onViewKnowledge}>
                      🗺️ View Knowledge
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setIsMinimized(false)}>
                      <ChevronDown className="w-4 h-4 mr-2" />
                      Show Full Results
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onExit}>
                      Exit Quiz
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Full view - complete results card
  return (
    <div className="absolute inset-0 z-modal flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-500 pointer-events-auto">
      <Card className="bg-card backdrop-blur-lg shadow-2xl max-w-md w-full mx-4 animate-in slide-in-from-bottom-4 duration-500 border-2 relative">
        <CardContent className="p-8 text-center space-y-6">
          {/* Minimize Button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsMinimized(true)}
            className="absolute top-2 right-2 h-8 w-8 p-0"
            title="Minimize to explore map"
          >
            <ChevronUp className="w-4 h-4" />
          </Button>

          <h2 className="text-4xl font-bold">🎉 Quiz Complete!</h2>

          <div className="space-y-2">
            <div className="text-7xl font-bold text-primary">
              {Math.round(stats.accuracyRate)}%
            </div>
            <p className="text-lg text-muted-foreground">
              {stats.correctAnswers} correct out of {stats.totalQuestions} questions
            </p>
          </div>

          {/* Perfect Score Badge */}
          {stats.accuracyRate === 100 && (
            <div className="p-4 bg-green-100 dark:bg-green-950 rounded-lg border-2 border-green-300 dark:border-green-700">
              <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                🌟 Perfect Score! Knowledge state updated.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3">
            {/* Primary Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={onRestart} className="w-full">
                🔄 Play Again
              </Button>
              {onEditQuiz && (
                <Button size="lg" variant="secondary" onClick={onEditQuiz} className="w-full">
                  ✏️ Edit Quiz
                </Button>
              )}
            </div>

            {/* Secondary Actions */}
            <div className="flex gap-2 items-center justify-center text-sm text-muted-foreground">
              <button
                onClick={() => setIsMinimized(true)}
                className="hover:text-foreground transition-colors underline underline-offset-2"
              >
                Explore map
              </button>
              <span>·</span>
              <button
                onClick={onViewKnowledge}
                className="hover:text-foreground transition-colors underline underline-offset-2"
              >
                View knowledge
              </button>
              <span>·</span>
              <button
                onClick={onExit}
                className="hover:text-foreground transition-colors underline underline-offset-2"
              >
                Exit
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
